import React from 'react';

import { createTheme, ThemeProvider, useTheme, Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { InterestDraft, Timeline, Measures } from './interestDraft';
import { StepChoice, StepContact, StepThanks } from './InterestSteps';

const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

// White sheet: dark text instead of the app's light-on-dark typography
const sheetTheme = (outer: Theme): Theme => createTheme(outer, {
    typography: {
        body1: { color: '#1f2a24' },
        body2: { color: '#1f2a24' },
        caption: { color: '#5f6b64' },
    },
    components: {
        MuiIconButton: { styleOverrides: { root: { color: '#1f2a24' } } },
    },
});

export type InterestTrigger = 'erdekel' | 'kerdezek';

/** GA funnel event (spec §7); no PII. Silent when gtag is missing / blocked. */
export const trackInterest = (event: string, quoteId: number, params: Record<string, string | number | boolean> = {}): void => {
    try {
        const gtag = (window as any).gtag;
        if (typeof gtag !== 'function') return;
        const url = new URLSearchParams(window.location.search);
        gtag('event', event, {
            quote_id: quoteId,
            source: url.get('utm_source') ?? '',
            campaign: url.get('utm_campaign') ?? '',
            device: window.matchMedia('(max-width: 899.95px)').matches ? 'mobile' : 'desktop',
            ...params,
        });
    } catch {
        /* analytics must never break the form */
    }
};

interface InterestSheetProps {
    open: boolean;
    onClose: () => void;
    quoteId: number;
    reference: string;  // cart reference, '' = show the number
    trigger: InterestTrigger;
    draft: InterestDraft;
    updateDraft: (patch: Partial<InterestDraft>) => void;
}

/** „Érdekel” flow: bottom sheet on mobile, centered card on desktop; plan stays visible behind. */
const InterestSheet: React.FC<InterestSheetProps> = ({ open, onClose, quoteId, reference, trigger, draft, updateDraft: saveDraft }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { step } = draft;

    React.useEffect(() => {
        if (!open) return;
        trackInterest('erdekel_kattintas', quoteId, { trigger, resume_step: draft.step });
        // Kérdezek presets the choice
        if (trigger === 'kerdezek' && !draft.sent) trackInterest('erdekel_valasztas', quoteId, { intent: 'question' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Funnel events from draft transitions
    const updateDraft = (patch: Partial<InterestDraft>) => {
        if (patch.intent && patch.step === 2) {
            trackInterest('erdekel_valasztas', quoteId, { intent: patch.intent });
        }
        if (patch.sent && !draft.sent) {
            trackInterest('erdekel_bekuldve', quoteId, { intent: draft.intent ?? '', slot: draft.slot, has_message: draft.intent !== 'order' && !!draft.message.trim() });
        }
        saveDraft(patch);
    };

    const onExtraSent = (timeline: Timeline | null, measures: Measures | null, photos: number) => {
        trackInterest('kiegeszites_bekuldve', quoteId, { timeline: timeline ?? '', measures: measures ?? '', photos });
    };

    const header = (
        <Box sx={{ px: 2, pt: isMobile ? 1 : 2, flex: '0 0 auto' }}>
            {isMobile && <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#d5d9d6', mx: 'auto', mb: 1 }} />}
            <Stack direction="row" alignItems="center" sx={{ minHeight: 40 }}>
                {step === 2 ? (
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => updateDraft({ step: 1 })}
                        sx={{ color: '#1f2a24', textTransform: 'none', ml: -1 }}
                    >
                        Vissza
                    </Button>
                ) : step === 1 ? (
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>1 / 2 lépés</Typography>
                ) : null}
                <Box sx={{ flex: 1 }} />
                {step === 2 && <Typography variant="caption" sx={{ fontWeight: 600, mr: 1 }}>2 / 2 lépés</Typography>}
                <IconButton aria-label="Bezárás" onClick={onClose} edge="end"><CloseIcon /></IconButton>
            </Stack>
            {step !== 3 && (
                <Stack direction="row" gap={0.5} sx={{ mt: 0.5 }}>
                    {[1, 2].map((i) => (
                        <Box key={i} sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: i <= step ? 'primary.main' : '#e3e7e4' }} />
                    ))}
                </Stack>
            )}
        </Box>
    );

    const content = (
        <>
            {header}
            {step === 1 && <StepChoice quoteId={quoteId} reference={reference} draft={draft} updateDraft={updateDraft} />}
            {step === 2 && <StepContact draft={draft} updateDraft={updateDraft} />}
            {step === 3 && <StepThanks draft={draft} updateDraft={updateDraft} onClose={onClose} onExtraSent={onExtraSent} />}
        </>
    );

    const paperSx = { bgcolor: '#fff', color: '#1f2a24', display: 'flex', flexDirection: 'column', overflow: 'hidden' } as const;

    return (
        <ThemeProvider theme={sheetTheme}>
            {isMobile ? (
                <SwipeableDrawer
                    anchor="bottom"
                    open={open}
                    onClose={onClose}
                    onOpen={() => {}}
                    disableSwipeToOpen
                    disableDiscovery
                    disableBackdropTransition={!iOS}
                    slotProps={{
                        paper: {
                            sx: {
                                ...paperSx,
                                borderTopLeftRadius: 16,
                                borderTopRightRadius: 16,
                                maxHeight: '92dvh',
                                height: step === 1 ? 'auto' : '92dvh',
                                pb: 'env(safe-area-inset-bottom)',
                            },
                        },
                        backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.35)' } },
                    }}
                >
                    {content}
                </SwipeableDrawer>
            ) : (
                <Dialog
                    open={open}
                    onClose={onClose}
                    maxWidth="xs"
                    fullWidth
                    slotProps={{
                        paper: { sx: { ...paperSx, borderRadius: 3, maxHeight: 'min(90vh, 780px)' } },
                        backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.35)' } },
                    }}
                >
                    {content}
                </Dialog>
            )}
        </ThemeProvider>
    );
};

export default InterestSheet;
